import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { BookOpenIcon, Cog6ToothIcon, ComputerDesktopIcon, DocumentTextIcon, MoonIcon, PowerIcon, ShieldCheckIcon, SunIcon } from '@heroicons/react/24/outline';
import { logoutUser } from '../slices/authSlice';
import { selectTheme, setTheme } from '../slices/preferencesSlice';
import ConfirmDialog from './ConfirmDialog';

const THEMES = [
  { value: 'light', label: 'Light', Icon: SunIcon },
  { value: 'dark', label: 'Dark', Icon: MoonIcon },
  { value: 'system', label: 'System', Icon: ComputerDesktopIcon },
];

// Popover (not Menu): Menu's MenuItems intercepts Tab and closes itself,
// skipping non-MenuItem children like the theme radiogroup entirely. Popover
// leaves normal Tab order alone, so every control in the panel is reachable.
const itemClass = 'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-gray-800 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-gray-100 dark:hover:bg-gray-600';
const iconClass = 'h-[18px] w-[18px] flex-none text-gray-500 dark:text-gray-300';

// Desktop identity: today's email-over-alias label, now a button that opens
// the account shortcuts.
export default function AccountMenu({ email, username, roleLabel }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector(selectTheme);
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <Popover as="div" className="relative hidden lg:block">
        <PopoverButton
          aria-label="Account menu"
          className="flex max-w-[260px] flex-col items-center rounded-md px-2.5 py-1 text-sm hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:hover:bg-gray-800"
        >
          <span className="max-w-full truncate font-semibold text-gray-900 dark:text-gray-100">{email}</span>
          <span className="max-w-full truncate text-xs text-brand-600 dark:text-brand-400">{username}</span>
        </PopoverButton>
        <PopoverPanel className="absolute right-0 top-full z-50 mt-1 w-72 rounded-lg bg-white p-1.5 shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-gray-700 dark:ring-white/20">
          {({ close }) => (
            <>
              <div className="px-2.5 pb-2.5 pt-1.5">
                <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{email}</p>
                <p className="truncate text-xs text-brand-600 dark:text-brand-400">{username}{roleLabel ? ` · ${roleLabel}` : ''}</p>
              </div>
              <div className="my-1 h-px bg-gray-200/70 dark:bg-gray-600/70" />
              <Link to="/settings" onClick={close} className={itemClass}>
                <Cog6ToothIcon className={iconClass} aria-hidden="true" />Settings
              </Link>
              <div className="flex items-center justify-between px-2.5 py-1.5 text-sm text-gray-800 dark:text-gray-100">
                <span>Theme</span>
                <div role="radiogroup" aria-label="Theme" className="inline-flex rounded-lg bg-gray-100 p-0.5 dark:bg-gray-800">
                  {THEMES.map(({ value, label, Icon }) => (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={theme === value}
                      aria-label={label}
                      onClick={() => dispatch(setTheme(value))}
                      className={`flex h-7 w-8 items-center justify-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${theme === value ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-600 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}
                    >
                      <Icon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              </div>
              <a href="/docs/" target="_blank" rel="noopener noreferrer" onClick={close} className={itemClass}>
                <BookOpenIcon className={iconClass} aria-hidden="true" />Forecaster docs<span className="ml-auto text-xs text-gray-400">↗</span>
              </a>
              <a href="/terms" target="_blank" rel="noopener noreferrer" onClick={close} className={itemClass}>
                <DocumentTextIcon className={iconClass} aria-hidden="true" />Terms of Use<span className="ml-auto text-xs text-gray-400">↗</span>
              </a>
              <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={close} className={itemClass}>
                <ShieldCheckIcon className={iconClass} aria-hidden="true" />Privacy Policy<span className="ml-auto text-xs text-gray-400">↗</span>
              </a>
              <div className="my-1 h-px bg-gray-200/70 dark:bg-gray-600/70" />
              <button
                type="button"
                onClick={() => { setConfirmOpen(true); close(); }}
                className={itemClass}
              >
                <PowerIcon className={iconClass} aria-hidden="true" />Sign out
              </button>
            </>
          )}
        </PopoverPanel>
      </Popover>
      <ConfirmDialog
        open={confirmOpen}
        title="Sign Out"
        description="Are you sure you want to sign out?"
        confirmText="Sign Out"
        cancelText="Cancel"
        onConfirm={() => { dispatch(logoutUser()); setConfirmOpen(false); navigate('/', { replace: true }); }}
        onCancel={() => setConfirmOpen(false)}
        iconBg="bg-red-100"
        iconColor="text-red-600"
      />
    </>
  );
}
